import { useEffect, useState } from 'react';
import { useQuery, onlineManager, skipToken } from '@tanstack/react-query';
import { formatQty, type ReorderPlanInput, type SuggestionScope } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import {
  readReorderWrite,
  type ReorderWriteRead,
} from '../../lib/reorder-write-recovery';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Toolbar } from '../ui/Page';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  shownState,
  refreshFailed,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { useSession } from '../session';
import { RestockSendSheet, RestockCheckSheet } from './RestockSheet';
import {
  ReorderPlanSheet,
  type ReorderPlan,
  type ReorderReview,
} from './ReorderPlanSheet';
import { money } from '../money';
import { ReorderSuggestionsSheet } from './ReorderSuggestionsSheet';

export function ReorderTab({ onOpenTab }: { onOpenTab?: (tab: string) => void } = {}) {
  const session = useSession();
  return (
    <ReorderOffice onOpenTab={onOpenTab} key={`${session.orgId}:${session.userId}`} />
  );
}
function ReorderOffice({ onOpenTab }: { onOpenTab?: (tab: string) => void }) {
  const session = useSession(),
    owner = { orgId: session.orgId, userId: session.userId },
    trpc = useTRPC();
  const [feeder, setFeeder] = useState(''),
    [skippedVans, setSkippedVans] = useState<Set<string>>(new Set()),
    [skippedBuys, setSkippedBuys] = useState<Set<string>>(new Set());
  const [review, setReview] = useState<ReorderReview | null>(null),
    [suggestionScope, setSuggestionScope] = useState<
      SuggestionScope | null | undefined
    >(),
    [recover, setRecover] = useState(false),
    [singleVan, setSingleVan] = useState<string | null>(null),
    [check, setCheck] = useState<string | null>(null);
  const [saved, setSaved] = useState<ReorderWriteRead>(() => readReorderWrite(owner)),
    [online, setOnline] = useState(onlineManager.isOnline());
  useEffect(() => onlineManager.subscribe(setOnline), []);
  const refreshSaved = () => setSaved(readReorderWrite(owner));
  useEffect(() => {
    window.addEventListener('storage', refreshSaved);
    window.addEventListener('focus', refreshSaved);
    return () => {
      window.removeEventListener('storage', refreshSaved);
      window.removeEventListener('focus', refreshSaved);
    };
  }, [owner.orgId, owner.userId]);
  const input: ReorderPlanInput = feeder
    ? { feederIds: [feeder], acknowledgeNegative: false }
    : { acknowledgeNegative: false };
  const query = useQuery(trpc.restocks.reorderPlan.queryOptions(input));
  const [knownFeeders, setKnownFeeders] = useState<ReorderPlan['feeders']>([]);
  useEffect(() => {
    if (!feeder && query.data) setKnownFeeders(query.data.feeders);
  }, [feeder, query.data]);
  const unchecked = useQuery(trpc.restocks.list.queryOptions({}));
  const data = query.data;
  const allHintPairs = [
    ...new Map(
      [
        ...(data?.vans.flatMap((v) =>
          v.items.map((i) => ({ locationId: v.vanId, itemId: i.itemId })),
        ) ?? []),
        ...(data?.buys.map((b) => ({ locationId: b.feederId, itemId: b.itemId })) ?? []),
      ].map((p) => [`${p.locationId}|${p.itemId}`, p]),
    ).values(),
  ];
  const hints = useQuery(
    trpc.restocks.suggestionHints.queryOptions(
      allHintPairs.length ? { pairs: allHintPairs.slice(0, 5000) } : skipToken,
      { enabled: online },
    ),
  );
  const hintByPair = new Map(
    hints.data?.hints.map((h) => [`${h.locationId}|${h.itemId}`, h]),
  );
  const usageHint = (locationId: string, itemId: string) => {
    const h = hintByPair.get(`${locationId}|${itemId}`);
    if (!h) return null;
    const label =
      h.status === 'actionable' &&
      h.suggestedKeepHundredths !== null &&
      h.suggestedFillHundredths !== null
        ? `Suggest ${formatQty(h.suggestedKeepHundredths)} / ${formatQty(h.suggestedFillHundredths)}`
        : h.status === 'matches_use'
          ? 'Matches use'
          : h.status === 'not_enough_usage'
            ? 'Not enough usage'
            : 'Usage unavailable';
    return h.scope ? (
      <button
        className="block min-h-11 max-w-44 whitespace-normal text-left text-xs underline"
        disabled={!online}
        title={h.explanation}
        onClick={() => setSuggestionScope(h.scope!)}
      >
        {label}
      </button>
    ) : (
      <p className="whitespace-normal text-xs text-muted-foreground">{label}</p>
    );
  };
  const state = shownState(query, query.data);
  const blocked = saved.status !== 'empty';
  const stale =
    refreshFailed(query, query.data) || query.isFetching || query.isError || !online;
  const selectedVans =
    data?.vans.filter((v) => v.feederId && !skippedVans.has(v.vanId)) ?? [];
  const chosenBuys =
    data?.buys.filter((b) => b.orderable && !skippedBuys.has(b.key)) ?? [];
  const openReview = (action: 'send' | 'draft') => {
    if (!data || stale || blocked) return;
    const feederIds = [
      ...new Set(
        action === 'send'
          ? selectedVans.map((v) => v.feederId!)
          : chosenBuys.map((b) => b.feederId),
      ),
    ].sort();
    const scope = new Set(feederIds);
    setReview({
      action,
      input: {
        feederIds,
        sends: data.proposal.sends.filter(
          (v) =>
            selectedVans.some((s) => s.vanId === v.vanId) &&
            scope.has(selectedVans.find((s) => s.vanId === v.vanId)!.feederId!),
        ),
        buyItems:
          action === 'draft'
            ? chosenBuys.map((b) => ({ feederId: b.feederId, itemId: b.itemId }))
            : [],
        acknowledgeNegative: false,
      },
    });
  };
  const toggle = (set: (v: (s: Set<string>) => Set<string>) => void, id: string) =>
    set((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const uncheckedPhase = shownState(unchecked, unchecked.data);
  const incoming = (rows: ReorderPlan['buys'][number]['incoming']) =>
    rows.map((p, n) => (
      <p className="text-xs text-muted-foreground" key={`${p.purchaseOrderId}:${n}`}>
        PO-{p.number}
        {p.status === 'draft' ? ' (draft, not sent)' : ''}:{' '}
        {formatQty(p.outstandingHundredths)}
      </p>
    ));
  type Item = ReorderPlan['vans'][number]['items'][number] & { vanId: string };
  const vanColumns: DataTableColumn<Item>[] = [
    {
      key: 'name',
      label: 'Part',
      value: (i) => i.name,
      cell: (i) => (
        <div>
          <p>{i.name}</p>
          {incoming(i.incoming)}
        </div>
      ),
    },
    ...(
      [
        'onHandHundredths',
        'onOrderHundredths',
        'keepAtLeastHundredths',
        'fillToHundredths',
        'owedHundredths',
        'askHundredths',
        'sendHundredths',
        'waitingHundredths',
      ] as const
    ).map((key, n) => ({
      key,
      label: ['Have', 'Coming', 'Keep', 'Fill', 'Owed', 'Ask', 'Send', 'Waiting'][n]!,
      align: 'right' as const,
      value: (i: Item) => i[key],
      cell: (i: Item) => (
        <div>
          {i[key] === null ? '—' : formatQty(i[key]!)}
          {key === 'keepAtLeastHundredths' ? usageHint(i.vanId, i.itemId) : null}
        </div>
      ),
    })),
  ];
  type Buy = ReorderPlan['buys'][number];
  const buyColumns: DataTableColumn<Buy>[] = [
    {
      key: 'name',
      label: 'Part',
      value: (b) => b.name,
      cell: (b) => (
        <div>
          <p>{b.name}</p>
          {usageHint(b.feederId, b.itemId)}
          {incoming(b.incoming)}
          <p className="text-xs">
            {b.missingCost
              ? 'Price unknown'
              : 'unitCostCents' in b && typeof b.unitCostCents === 'number'
                ? `${money(b.unitCostCents)} each`
                : 'Price withheld'}
          </p>
          {!b.orderable ? (
            <p className="text-xs text-[var(--sw-warning-text)]">
              Choose an active vendor and tracked part
            </p>
          ) : null}
        </div>
      ),
    },
    ...(
      [
        'forVansHundredths',
        'forShelfHundredths',
        'packExcessHundredths',
        'buyHundredths',
      ] as const
    ).map((key, n) => ({
      key,
      label: ['For vans', 'For shelf', 'Pack excess', 'Buy'][n]!,
      align: 'right' as const,
      value: (b: Buy) => b[key],
      cell: (b: Buy) => formatQty(b[key]),
    })),
    {
      key: 'select',
      label: 'Choose',
      value: () => null,
      cell: (b) => (
        <input
          className="min-h-11 min-w-11"
          aria-label={`Buy ${b.name} for ${b.destinationName}`}
          type="checkbox"
          disabled={!b.orderable || blocked}
          checked={b.orderable && !skippedBuys.has(b.key)}
          onChange={() => toggle(setSkippedBuys, b.key)}
        />
      ),
    },
  ];
  const groups = new Map<string, Buy[]>();
  for (const b of data?.buys ?? []) {
    const k = JSON.stringify([b.feederId, b.vendorId]);
    const rows = groups.get(k) ?? [];
    rows.push(b);
    groups.set(k, rows);
  }
  return (
    <div className="min-w-0 space-y-5">
      {blocked ? (
        <Notice tone="warn">
          A saved inventory request needs resolution before another batch.{' '}
          <Button className="min-h-11" variant="ghost" onClick={() => setRecover(true)}>
            Recover saved inventory request
          </Button>
        </Notice>
      ) : null}
      <section aria-label="Unchecked restocks" className="space-y-2">
        <h2 className="font-heading text-sm font-semibold">Unchecked restocks</h2>
        <StaleNotice
          show={refreshFailed(unchecked, unchecked.data)}
          onRetry={() => void unchecked.refetch()}
        />
        {uncheckedPhase !== 'ready' ? (
          <UnresolvedState
            state={uncheckedPhase}
            what="unchecked restocks"
            icon={IconInventory}
            error={unchecked.error}
            onRetry={() => void unchecked.refetch()}
          />
        ) : (
          unchecked.data?.items
            .filter((r) => r.state === 'sent')
            .map((r) => (
              <Button
                key={r.restockId}
                className="min-h-11 mr-2"
                variant="ghost"
                onClick={() => setCheck(r.restockId)}
              >
                R-{r.restockNumber} · {r.vanName}
              </Button>
            ))
        )}
        {unchecked.data?.uncheckedCount === 0 ? (
          <p className="text-xs text-muted-foreground">All restocks are checked.</p>
        ) : null}
        {unchecked.data?.hasMore ? (
          <p className="text-xs">
            More restocks are available in each van’s restock history.
          </p>
        ) : null}
      </section>
      <Toolbar>
        <Field label="Feeder scope">
          <select
            className={`${field} min-h-11 max-w-64`}
            value={feeder}
            onChange={(e) => {
              setFeeder(e.target.value);
              setSkippedBuys(new Set());
              setSkippedVans(new Set());
            }}
          >
            <option value="">All feeders</option>
            {(feeder ? knownFeeders : data?.feeders)?.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <Button className="min-h-11" variant="ghost" onClick={() => void query.refetch()}>
          Refresh plan
        </Button>
        <Button
          className="min-h-11"
          variant="ghost"
          disabled={!online}
          onClick={() => setSuggestionScope(null)}
        >
          Review usage suggestions
        </Button>
      </Toolbar>
      {hints.isError ? (
        <Notice tone="warn">
          Usage hints could not refresh. Send and Buy remain available; open usage
          suggestions to retry their review.
        </Notice>
      ) : null}
      {allHintPairs.length > 5000 ? (
        <Notice tone="info">
          Usage hints are shown for the first5,000 visible pairs. Open usage suggestions
          to review a specific owner.
        </Notice>
      ) : null}
      <StaleNotice
        show={refreshFailed(query, query.data)}
        onRetry={() => void query.refetch()}
      />
      {!online ? (
        <Notice tone="warn">
          Offline view. Connect and refresh before reviewing an office Send or Draft.
        </Notice>
      ) : null}
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          what="what needs reordering"
          icon={IconInventory}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : data ? (
        <>
          {data.vans.length === 0 && data.buys.length === 0 ? (
            <p>
              Nothing to reorder. Set up van Keep and Fill quantities under Van templates
              → Vans.
            </p>
          ) : null}
          {data.vans.map((v) => (
            <section key={v.vanId} className="min-w-0 space-y-2">
              <h2 className="font-heading font-semibold">
                Restock vans from {v.feederName ?? 'an unconfigured feeder'}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">{v.vanName}</h3>
                {v.feederId ? (
                  <Field label={`Include ${v.vanName}`}>
                    <input
                      className="min-h-11 min-w-11"
                      type="checkbox"
                      checked={!skippedVans.has(v.vanId)}
                      disabled={blocked}
                      onChange={() => toggle(setSkippedVans, v.vanId)}
                    />
                  </Field>
                ) : (
                  <Button
                    className="min-h-11"
                    variant="ghost"
                    onClick={() => onOpenTab?.('vans')}
                  >
                    Choose feeder in Van templates
                  </Button>
                )}
                <Button
                  className="min-h-11"
                  variant="ghost"
                  onClick={() => {
                    if (
                      saved.status === 'unreadable' ||
                      (saved.status === 'ready' &&
                        saved.attempt.action === 'send' &&
                        saved.attempt.request.sends.some((s) => s.vanId === v.vanId))
                    )
                      setRecover(true);
                    else setSingleVan(v.vanId);
                  }}
                >
                  Send to {v.vanName}
                </Button>
              </div>
              <DataTable
                tableId={`reorder-van-${v.vanId}`}
                columns={vanColumns}
                rows={v.items.map((i) => ({ ...i, vanId: v.vanId }))}
                rowKey={(i) => i.itemId}
                empty={<p className="text-sm">No watched or owed parts on this van.</p>}
              />
            </section>
          ))}
          <Button
            className="min-h-11"
            disabled={
              stale ||
              blocked ||
              !selectedVans.some((v) => v.items.some((i) => i.sendHundredths > 0))
            }
            onClick={() => openReview('send')}
          >
            Review Send · {selectedVans.length} vans
          </Button>
          {[...groups.entries()].map(([k, rows]) => (
            <section key={k} className="min-w-0 space-y-2">
              <h2 className="font-heading font-semibold">
                Buy for {rows[0]!.destinationName}
              </h2>
              <h3 className="text-sm">
                {rows[0]!.vendorName ?? 'No vendor set'} → {rows[0]!.destinationName}
              </h3>
              <DataTable
                tableId={`reorder-buy-${rows[0]!.feederId}-${rows[0]!.vendorId ?? 'none'}`}
                columns={buyColumns}
                rows={rows}
                empty={<p>No purchases in this group.</p>}
                rowKey={(b) => b.key}
              />
            </section>
          ))}
          {chosenBuys.length > 200 ? (
            <Notice tone="warn">
              Choose at most 200 Buy lines for one reviewed draft batch. Uncheck parts to
              reduce the selection; nothing is split or dropped automatically.
            </Notice>
          ) : null}
          <Button
            className="min-h-11"
            disabled={stale || blocked || !chosenBuys.length || chosenBuys.length > 200}
            onClick={() => openReview('draft')}
          >
            Review Draft · {chosenBuys.length} parts
          </Button>
          {data.knownSubtotalCents !== undefined ? (
            <p className="text-xs text-muted-foreground">
              Plan known subtotal {money(data.knownSubtotalCents)} ·{' '}
              {data.unknownCostCount} unknown-price lines. Review shows the exact selected
              vendor and destination groups. Drafts only; nothing sent.
            </p>
          ) : null}
          {data.outsidePoints.length || data.unmappedSupply.length ? (
            <section className="space-y-2">
              <h2 className="font-semibold">Outside this feeder plan</h2>
              {data.outsidePoints.map((p) => (
                <p className="text-sm" key={`${p.locationId}|${p.itemId}`}>
                  {p.locationName} · {p.name}
                </p>
              ))}
              {data.unmappedSupply.map((p) => (
                <p className="text-sm" key={`${p.locationId}|${p.itemId}`}>
                  {p.locationName} · {formatQty(p.ownHundredths)} unmapped stock
                </p>
              ))}
              <p className="text-sm">
                These locations need a manual stock or purchase decision.{' '}
                <Button
                  className="min-h-11"
                  variant="ghost"
                  onClick={() => onOpenTab?.('stock')}
                >
                  Open Stock
                </Button>{' '}
                ·{' '}
                <Button
                  className="min-h-11"
                  variant="ghost"
                  onClick={() => onOpenTab?.('orders')}
                >
                  Open Purchase orders
                </Button>
              </p>
            </section>
          ) : null}
        </>
      ) : null}
      {review || recover ? (
        <ReorderPlanSheet
          owner={owner}
          review={review ?? undefined}
          saved={saved}
          onStorageChange={refreshSaved}
          onClose={() => {
            setReview(null);
            setRecover(false);
            refreshSaved();
          }}
        />
      ) : null}
      {singleVan ? (
        <RestockSendSheet
          key={`${owner.orgId}:${owner.userId}:${singleVan}`}
          vanId={singleVan}
          onClose={() => setSingleVan(null)}
        />
      ) : null}
      {check ? (
        <RestockCheckSheet restockId={check} onClose={() => setCheck(null)} />
      ) : null}
      {suggestionScope !== undefined ? (
        <ReorderSuggestionsSheet
          onSetup={() => {
            setSuggestionScope(undefined);
            onOpenTab?.('stock');
          }}
          initialScope={suggestionScope ?? undefined}
          onClose={() => setSuggestionScope(undefined)}
        />
      ) : null}
    </div>
  );
}
